import { isEndpointDisabled } from '../http/errors.ts';
import { parseUrl } from './urls.ts';
import {
  COMPANION_CAPTION_PREFIX,
  DOCUMENTED_CAPTION_PREFIX,
  UNKNOWN_CAPABILITIES,
  type CapabilityState,
  type CaptionPathPrefix,
  type InstanceCapabilities,
  type InstanceSoftware,
} from './capabilities.ts';

/**
 * §3's instance validation, which is a **5-step probe** and not a ping.
 *
 * The reason it is five steps is in §3's first line: *`/api/v1/stats` alone is a false-positive
 * machine*. It returns 200 on instances whose video API is dead, and this build can measure both ends
 * of that — `https://example.com/api/v1/stats` answers `404 text/html`, so even "did it answer JSON"
 * is a question worth asking, while the instance's own stats block is `{"software":{…}}` and nothing
 * else because statistics are disabled. Neither says anything about whether a video plays.
 *
 * **Step 3 is the gate.** If `/api/v1/videos/dQw4w9WgXcQ` fails, the instance is unusable and steps 4
 * and 5 are skipped rather than reported as failures — they would be describing a machine that already
 * cannot serve the app.
 *
 * ### It runs in core, over an injected fetcher, and that is what makes it usable
 *
 * The probe has to run against an instance that is **not** the configured one — that is the whole point
 * of the settings page's "check this instance" button — so it cannot be written against `ApiTransport`,
 * which is built per instance. {@link ProbeFetch} is the smallest seam that works: give it a path, get
 * back a status, a body and a *reason* if there was no answer at all. The platform supplies it (one
 * `fetch`, no credential, and §3's `mode: 'no-cors'` retry to tell CORS from downtime), and this file
 * stays pure — so a Node test drives the same five steps with a fake, and a TV runs them with no CORS
 * concept at all.
 *
 * ### What §3 gets wrong about step 4, measured
 *
 * §3 says step 4 *derives what you can from that payload (companion? adaptive? popular enabled? auth
 * present?)*. Two of those four are **not in the payload**:
 *
 * - **`popular enabled?`** appears nowhere in a video record. It costs its own request, and the only
 *   thing that answers it is `/api/v1/popular` refusing with `Administrator has disabled this
 *   endpoint.` — which is exactly what `isEndpointDisabled` already recognises for the Home page.
 * - **`auth present?`** likewise. Unauthenticated, every path under `/api/v1/auth/` answers `403
 *   {"error":"Request must be authenticated"}` — *including paths that do not exist* — so the probe
 *   learns that the subsystem is mounted and deliberately claims no more than that.
 *
 * What the payload *does* answer is `adaptiveFormats`, `dashUrl`, whether captions are listed at all,
 * and whether media URLs were rewritten to the instance (§2's mandatory `?local=true`). **Companion is
 * not among them either**: on this instance the proxied media URL is `/videoplayback`, with no
 * `/companion` prefix anywhere in the JSON, so the companion path is only ever proven by step 5
 * fetching caption bytes.
 */

/** The video §3 names. A well-known, permanently available id — the gate is about the *instance*. */
export const PROBE_VIDEO_ID = 'dQw4w9WgXcQ';

export type ProbeFetchFailureKind =
  /** No answer, and a `mode: 'no-cors'` retry did answer — so it is policy, not downtime (§3). */
  | 'cors'
  | 'network'
  | 'aborted';

export type ProbeFetchResult =
  | {
      readonly ok: true;
      readonly status: number;
      readonly body: string;
      readonly contentType: string | null;
    }
  | { readonly ok: false; readonly kind: ProbeFetchFailureKind };

/**
 * One request against the instance being probed. Paths only — the caller owns the base URL, which is
 * what lets the same function probe a candidate the app is not configured for.
 */
export type ProbeFetch = (path: string) => Promise<ProbeFetchResult>;

export type ProbeStepId = 'stats' | 'public-api' | 'video' | 'features' | 'captions';

/**
 * Machine-readable findings (R7 — core ships no display strings). The settings page turns each id into
 * a sentence; the ids are what a test asserts on and what a future TV shows in its own words.
 */
export type ProbeFinding =
  | 'unreachable'
  | 'cors-blocked'
  | 'aborted'
  | 'http-error'
  | 'empty-body'
  | 'not-json'
  | 'not-invidious'
  /** A software block with no `usage` — statistics are switched off. Informational, not a problem. */
  | 'statistics-disabled'
  | 'no-adaptive-formats'
  | 'no-dash-manifest'
  | 'no-captions-listed'
  /** `dashUrl?local=true` did not come back as an MPD — the manifest §7's player loads. */
  | 'manifest-unreadable'
  | 'popular-disabled'
  | 'auth-absent'
  /** The documented caption path answered, which is the *unusual* case worth naming (§2 trap 1). */
  | 'captions-documented-path'
  | 'captions-companion-path'
  | 'captions-unavailable';

export type ProbeOutcome =
  | 'ok'
  /** It answered, and something about the answer is worth saying. Never fatal. */
  | 'warning'
  /** Only step 3 can be fatal; every other failure is a warning. */
  | 'failed'
  | 'skipped';

export interface ProbeStepResult {
  readonly id: ProbeStepId;
  readonly outcome: ProbeOutcome;
  readonly findings: readonly ProbeFinding[];
  /** HTTP status of the step's *own* request, or `null` when there was no answer. */
  readonly status: number | null;
}

export interface InstanceProbeReport {
  /** Step 3 passed: this instance can serve the app. Every other step is diagnosis. */
  readonly usable: boolean;
  readonly steps: readonly ProbeStepResult[];
  readonly capabilities: InstanceCapabilities;
}

/** R4 — `JSON.parse` is the one place core is allowed to do it, because the probe *is* the reader. */
function jsonOf(result: ProbeFetchResult): unknown {
  if (!result.ok || result.body.trim() === '') return undefined;
  try {
    return JSON.parse(result.body) as unknown;
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The findings that describe *not getting an answer*, shared by every step. */
function transportFindings(result: ProbeFetchResult): readonly ProbeFinding[] {
  if (result.ok) return [];
  if (result.kind === 'cors') return ['cors-blocked'];
  if (result.kind === 'aborted') return ['aborted'];
  return ['unreachable'];
}

function statusOf(result: ProbeFetchResult): number | null {
  return result.ok ? result.status : null;
}

// ---------------------------------------------------------------------------
// Step 1 — is it Invidious, which version
// ---------------------------------------------------------------------------

function readSoftware(raw: unknown): InstanceSoftware | null {
  if (!isRecord(raw)) return null;
  const software = raw['software'];
  if (!isRecord(software)) return null;
  const name = software['name'];
  const version = software['version'];
  if (typeof name !== 'string' || typeof version !== 'string') return null;
  const branch = software['branch'];
  return { name, version, branch: typeof branch === 'string' ? branch : null };
}

function probeStats(result: ProbeFetchResult): {
  readonly step: ProbeStepResult;
  readonly software: InstanceSoftware | null;
} {
  const findings = [...transportFindings(result)];
  if (!result.ok) {
    return { step: { id: 'stats', outcome: 'warning', findings, status: null }, software: null };
  }

  if (result.status !== 200) findings.push('http-error');
  if (result.body.trim() === '') findings.push('empty-body');

  const raw = jsonOf(result);
  if (raw === undefined) {
    // `https://example.com/api/v1/stats` → `404 text/html`. A status check alone reads that as "down".
    if (!findings.includes('empty-body')) findings.push('not-json');
    return { step: { id: 'stats', outcome: 'warning', findings, status: result.status }, software: null };
  }

  const software = readSoftware(raw);
  if (software === null || software.name.toLowerCase() !== 'invidious') {
    findings.push('not-invidious');
    return { step: { id: 'stats', outcome: 'warning', findings, status: result.status }, software };
  }

  // This instance's own answer: a software block and nothing else, because `statistics_enabled` is
  // off. Worth reporting so "no usage figures" does not read as a broken probe.
  if (!isRecord(raw) || !isRecord(raw['usage'])) findings.push('statistics-disabled');

  return {
    step: { id: 'stats', outcome: outcomeFor(findings), findings, status: result.status },
    software,
  };
}

/**
 * Findings that are worth reporting and are not problems. `statistics-disabled` is the whole set: the
 * instance this project runs against has statistics off, and a probe that graded its own instance as
 * "warning" on step 1 would be crying wolf on every run.
 */
const INFORMATIONAL_FINDINGS: readonly ProbeFinding[] = ['statistics-disabled'];

function outcomeFor(findings: readonly ProbeFinding[]): ProbeOutcome {
  return findings.some((finding) => !INFORMATIONAL_FINDINGS.includes(finding)) ? 'warning' : 'ok';
}

// ---------------------------------------------------------------------------
// Step 2 — is the public API reachable
// ---------------------------------------------------------------------------

function probePublicApi(result: ProbeFetchResult): {
  readonly step: ProbeStepResult;
  readonly trending: CapabilityState;
} {
  const findings = [...transportFindings(result)];
  if (!result.ok) {
    return { step: { id: 'public-api', outcome: 'warning', findings, status: null }, trending: 'unknown' };
  }

  if (result.status !== 200) findings.push('http-error');
  const raw = jsonOf(result);
  if (result.body.trim() === '') findings.push('empty-body');
  else if (!Array.isArray(raw)) findings.push('not-json');

  const serves = findings.length === 0;
  return {
    step: {
      id: 'public-api',
      outcome: serves ? 'ok' : 'warning',
      findings,
      status: result.status,
    },
    // `unknown` rather than `disabled`: Invidious has no switch for trending, so a failure here is a
    // region or an upstream problem (`feed/region.ts`) rather than a policy this probe can report.
    trending: serves ? 'serves' : 'unknown',
  };
}

// ---------------------------------------------------------------------------
// Step 3 — the gate, and step 4's derivations from its payload
// ---------------------------------------------------------------------------

export interface VideoDerivations {
  readonly adaptiveFormats: CapabilityState;
  /** The manifest path to *fetch*, already carrying §2's `?local=true`, or `null` if there is none. */
  readonly manifestPath: string | null;
  readonly findings: readonly ProbeFinding[];
}

/**
 * Step 4's derivable half — what the gate video's payload can be read for, and nothing more.
 *
 * Exported for the testkit, because these readings are the ones a payload change would silently alter.
 * Note what is **not** derived: whether media is proxied. `adaptiveFormats[].url` on a plain
 * `/api/v1/videos/{id}` is a direct googlevideo URL by design, and the app never fetches it — so the
 * question is answered by fetching the manifest instead (`capabilities.ts`, `dashManifest`).
 */
export function deriveFromVideo(raw: unknown): VideoDerivations {
  if (!isRecord(raw)) {
    return { adaptiveFormats: 'unknown', manifestPath: null, findings: [] };
  }

  const findings: ProbeFinding[] = [];
  const adaptive = raw['adaptiveFormats'];
  const hasAdaptive = Array.isArray(adaptive) && adaptive.length > 0;
  if (!hasAdaptive) findings.push('no-adaptive-formats');

  const dashUrl = raw['dashUrl'];
  const hasDash = typeof dashUrl === 'string' && dashUrl !== '';
  if (!hasDash) findings.push('no-dash-manifest');

  const captions = raw['captions'];
  if (!Array.isArray(captions) || captions.length === 0) findings.push('no-captions-listed');

  return {
    adaptiveFormats: hasAdaptive ? 'serves' : 'disabled',
    // The path only — `ProbeFetch` takes paths, and `dashUrl` is absolute on this instance. Anything
    // whose host is *not* the instance is refused rather than fetched: a probe must not follow a
    // manifest URL to a third party.
    manifestPath: hasDash && typeof dashUrl === 'string' ? manifestPathOf(dashUrl) : null,
    findings,
  };
}

/** `https://host/api/manifest/dash/id/X` → `/api/manifest/dash/id/X?local=true`. */
function manifestPathOf(dashUrl: string): string | null {
  const parts = parseUrl(dashUrl);
  if (parts === null || parts.path === '') return null;
  const query = parts.query === '' ? 'local=true' : `${parts.query}&local=true`;
  return `${parts.path}?${query}`;
}

// ---------------------------------------------------------------------------
// Step 5 — the caption path, which only bytes can answer
// ---------------------------------------------------------------------------

function nonEmpty(result: ProbeFetchResult): boolean {
  return result.ok && result.status === 200 && result.body.trim() !== '';
}

// ---------------------------------------------------------------------------
// The probe
// ---------------------------------------------------------------------------

/**
 * Run §3's five steps. Sequential because step 3 gates steps 4 and 5, and because a probe that fired
 * eight requests at once at an instance it has just been told about would be its own small denial of
 * service — §3's retry policy exists for the same reason.
 */
export async function probeInstance(fetchPath: ProbeFetch): Promise<InstanceProbeReport> {
  const stats = probeStats(await fetchPath('/api/v1/stats'));
  const publicApi = probePublicApi(await fetchPath('/api/v1/trending'));

  // Exactly what `api/videos.ts` asks for — no `?local=true`, because the app does not send it either
  // and a probe that asks a different question than the app cannot answer for the app. §2's `local=true`
  // belongs on the *manifest*, which is where the parser puts it and where step 4 checks it.
  const videoResult = await fetchPath(`/api/v1/videos/${PROBE_VIDEO_ID}`);
  const videoFindings = [...transportFindings(videoResult)];
  const videoRaw = jsonOf(videoResult);

  if (videoResult.ok) {
    if (videoResult.status !== 200) videoFindings.push('http-error');
    if (videoResult.body.trim() === '') videoFindings.push('empty-body');
    else if (videoRaw === undefined) videoFindings.push('not-json');
  }

  const videoUsable =
    videoResult.ok &&
    videoResult.status === 200 &&
    isRecord(videoRaw) &&
    videoRaw['videoId'] === PROBE_VIDEO_ID;

  if (!videoUsable) {
    // §3: "If this fails, the instance is unusable. Say so." Steps 4 and 5 are skipped rather than
    // failed — they would be diagnosing a machine that has already answered the only question.
    return {
      usable: false,
      steps: [
        stats.step,
        publicApi.step,
        {
          id: 'video',
          outcome: 'failed',
          findings: videoFindings,
          status: statusOf(videoResult),
        },
        { id: 'features', outcome: 'skipped', findings: [], status: null },
        { id: 'captions', outcome: 'skipped', findings: [], status: null },
      ],
      capabilities: { ...UNKNOWN_CAPABILITIES, trending: publicApi.trending, software: stats.software },
    };
  }

  const derived = deriveFromVideo(videoRaw);

  /*
   * Step 4's three questions that are **not** in the payload — one request each, none of them fatal.
   *
   * The manifest is the first, and it is the one §3 does not mention at all: `dashUrl` being a string
   * says the instance built a URL, not that it can build the document. `?local=true` is applied here
   * because that is what the player loads (§2, `parse/video.ts`), and the check is for an `<MPD` root
   * rather than a 200 — this API answers `200` with zero bytes in seven places, and the DASH manifest
   * endpoint is one of them.
   */
  const featureFindings: ProbeFinding[] = [];
  let dashManifest: CapabilityState = derived.adaptiveFormats === 'unknown' ? 'unknown' : 'disabled';
  if (derived.manifestPath !== null) {
    const manifest = await fetchPath(derived.manifestPath);
    if (manifest.ok && manifest.status === 200 && manifest.body.includes('<MPD')) {
      dashManifest = 'serves';
    } else {
      featureFindings.push('manifest-unreadable');
    }
  }

  const popularResult = await fetchPath('/api/v1/popular');
  let popular: CapabilityState = 'unknown';
  if (popularResult.ok && isEndpointDisabled(popularResult.status, popularResult.body)) {
    popular = 'disabled';
    featureFindings.push('popular-disabled');
  } else if (popularResult.ok && popularResult.status === 200 && Array.isArray(jsonOf(popularResult))) {
    popular = 'serves';
  }

  const authResult = await fetchPath('/api/v1/auth/subscriptions');
  let auth: CapabilityState = 'unknown';
  if (authResult.ok) {
    // 403 is the *expected* answer with no credential, and it is the one that proves the subsystem is
    // mounted. A 404 means these paths are not served at all.
    if (authResult.status === 403) auth = 'serves';
    else if (authResult.status === 404) {
      auth = 'disabled';
      featureFindings.push('auth-absent');
    }
  }

  const featureStep: ProbeStepResult = {
    id: 'features',
    outcome: derived.findings.length === 0 && featureFindings.length === 0 ? 'ok' : 'warning',
    findings: [...derived.findings, ...featureFindings],
    status: statusOf(videoResult),
  };

  // Step 5 — companion first, then the documented path, and *bytes* decide. Nothing in the JSON does.
  const companion = await fetchPath(`${COMPANION_CAPTION_PREFIX}/${PROBE_VIDEO_ID}?label=English`);
  let captionPathPrefix: CaptionPathPrefix | null = null;
  const captionFindings: ProbeFinding[] = [];
  let captionStatus = statusOf(companion);

  if (nonEmpty(companion)) {
    captionPathPrefix = COMPANION_CAPTION_PREFIX;
    captionFindings.push('captions-companion-path');
  } else {
    const documented = await fetchPath(`${DOCUMENTED_CAPTION_PREFIX}/${PROBE_VIDEO_ID}?label=English`);
    captionStatus = statusOf(documented);
    if (nonEmpty(documented)) {
      captionPathPrefix = DOCUMENTED_CAPTION_PREFIX;
      captionFindings.push('captions-documented-path');
    } else {
      captionFindings.push('captions-unavailable');
    }
  }

  return {
    usable: true,
    steps: [
      stats.step,
      publicApi.step,
      { id: 'video', outcome: 'ok', findings: [], status: videoResult.ok ? videoResult.status : null },
      featureStep,
      {
        id: 'captions',
        outcome: captionPathPrefix === null ? 'warning' : 'ok',
        findings: captionFindings,
        status: captionStatus,
      },
    ],
    capabilities: {
      captionPathPrefix,
      popular,
      trending: publicApi.trending,
      adaptiveFormats: derived.adaptiveFormats,
      dashManifest,
      auth,
      software: stats.software,
    },
  };
}

/**
 * Whether a probe result means "do not switch to this instance".
 *
 * **Exactly one thing is disqualifying**, and it is §3's own choice of gate rather than a policy this
 * file invents: `/api/v1/videos/dQw4w9WgXcQ` did not come back. Everything else — no popular, no
 * captions, an unreadable manifest, statistics off, a stats endpoint that is not even Invidious — is a
 * warning, because each of them describes an instance that still works less one feature.
 *
 * The manifest deliberately does *not* block, which is a judgement worth stating: `/companion/*`
 * answers 3–8 % of requests with another request's response (CLAUDE.md), so one failed manifest fetch
 * is as likely to be that as it is to be a broken instance, and refusing an instance over it would be a
 * coin flip wearing a diagnosis.
 */
export function probeBlocksUse(report: InstanceProbeReport): boolean {
  return !report.usable;
}

